import {
  CorporateBondId,
  CouponPayment,
  IssuerId,
  MoodysRating,
  OrderDirection,
  OrderTicketId,
  SpRating,
  TradeTicketId,
} from "@goodfill/domain-properties";
import { PresentedTradeTicket } from "./PresentedTradeTicket";
import { FilterFunction } from "../../../../filters/filters";
import { DateRange } from "../../../../filters/DateTimeRangeFilter";
import { DecimalRange } from "../../../../filters/DecimalRangeFilter";
import { ISIN_CRITERIA_KEY } from "../../../DataGridFilteredColumns/bond/IsinColumnDefinition";
import {
  ISSUER_ID_CRITERIA_KEY,
  issuerIdFilter,
} from "../../../DataGridFilteredColumns/bond/IssuerIdColumnDefinition";
import { MATURITY_CRITERIA_KEY } from "../../../DataGridFilteredColumns/bond/MaturityDateColumnDefinition";
import {
  MOODYS_RATING_CRITERIA_KEY,
  moodysRatingFilter,
} from "../../../DataGridFilteredColumns/bond/MoodysRatingColumnDefinition";
import { PAYMENT_RANK_CRITERIA_KEY } from "../../../DataGridFilteredColumns/bond/PaymentRankColumnDefinition";
import {
  SP_RATING_CRITERIA_KEY,
  spRatingFilter,
} from "../../../DataGridFilteredColumns/bond/SpRatingColumnDefinition";
import { TICKER_CRITERIA_KEY, tickersFilter } from "../../../DataGridFilteredColumns/bond/TickerColumnDefinition";
import {
  FACE_AMOUNT_CRITERIA_KEY,
  faceAmountFilter,
} from "../../../DataGridFilteredColumns/order/FaceAmountColumnDefinition";
import {
  ORDER_TICKET_ID_CRITERIA_KEY,
  orderTicketIdFilter,
} from "../../../DataGridFilteredColumns/order/OrderTicketIdColumnDefinition";
import { Range } from "../../../DataGridFilteredColumns/generic/RangeColumnDefinition";
import { COUPON_PAYMENT_CRITERIA_KEY } from "../../../DataGridFilteredColumns/bond/CouponPaymentColumnDefinition";
import {
  TRADE_TICKET_ID_CRITERIA_KEY,
  tradeTicketIdFilter,
} from "../../../DataGridFilteredColumns/tradeTicket/TradeTicketIdColumnDefinition";
import {
  LOADED_BOND_PRICE_CRITERIA_KEY,
  postFeePriceFilter,
} from "../../../DataGridFilteredColumns/order/PostFeePriceColumnDefinition";
import {
  TRADE_VALUE_CRITERIA_KEY,
  tradeValueFilter,
} from "../../../DataGridFilteredColumns/tradeTicket/TradeValueColumnDefinition";
import {
  SETTLEMENT_AMOUNT_CRITERIA_KEY,
  settlementAmountFilter,
} from "../../../DataGridFilteredColumns/tradeTicket/SettlementAmountValueColumnDefinition";
import {
  FINAL_COMMISSION_AMOUNT_CRITERIA_KEY,
  finalCommissionAmountFilter,
} from "../../../DataGridFilteredColumns/tradeTicket/FinalCommissionAmountColumnDefinition";

export interface PortfolioTradeHistoryFilter {
  tradeDateRange: DateRange;
  [MATURITY_CRITERIA_KEY]: DateRange;
  direction: OrderDirection | null;
  [ISIN_CRITERIA_KEY]: CorporateBondId[];
  [TICKER_CRITERIA_KEY]: string[];
  [ISSUER_ID_CRITERIA_KEY]: IssuerId[];
  [ORDER_TICKET_ID_CRITERIA_KEY]: OrderTicketId[];
  [FACE_AMOUNT_CRITERIA_KEY]: DecimalRange;
  [MOODYS_RATING_CRITERIA_KEY]: Range<MoodysRating>;
  [SP_RATING_CRITERIA_KEY]: Range<SpRating>;
  [PAYMENT_RANK_CRITERIA_KEY]: string[];
  [COUPON_PAYMENT_CRITERIA_KEY]: CouponPayment[];
  [TRADE_TICKET_ID_CRITERIA_KEY]: TradeTicketId[];
  [LOADED_BOND_PRICE_CRITERIA_KEY]: DecimalRange;
  [TRADE_VALUE_CRITERIA_KEY]: DecimalRange;
  [SETTLEMENT_AMOUNT_CRITERIA_KEY]: DecimalRange;
  [FINAL_COMMISSION_AMOUNT_CRITERIA_KEY]: DecimalRange;
}

export const defaultPortfolioTradeHistoryFilterConstraints: PortfolioTradeHistoryFilter = {
  tradeDateRange: [null, null],
  [MATURITY_CRITERIA_KEY]: [null, null],
  direction: null,
  [ISIN_CRITERIA_KEY]: [],
  [TICKER_CRITERIA_KEY]: [],
  [ISSUER_ID_CRITERIA_KEY]: [],
  [ORDER_TICKET_ID_CRITERIA_KEY]: [],
  [FACE_AMOUNT_CRITERIA_KEY]: [null, null],
  [MOODYS_RATING_CRITERIA_KEY]: [null, null],
  [SP_RATING_CRITERIA_KEY]: [null, null],
  [PAYMENT_RANK_CRITERIA_KEY]: [],
  [COUPON_PAYMENT_CRITERIA_KEY]: [],
  [TRADE_TICKET_ID_CRITERIA_KEY]: [],
  [LOADED_BOND_PRICE_CRITERIA_KEY]: [null, null],
  [TRADE_VALUE_CRITERIA_KEY]: [null, null],
  [SETTLEMENT_AMOUNT_CRITERIA_KEY]: [null, null],
  [FINAL_COMMISSION_AMOUNT_CRITERIA_KEY]: [null, null],
};

export const tradeDateFilter = (
  item: Pick<PresentedTradeTicket, "tradeDate">,
  constrains: Pick<PortfolioTradeHistoryFilter, "tradeDateRange">,
) => {
  const tradeDateSince = constrains.tradeDateRange[0]?.set({ hour: 0, minute: 0, second: 0 }) ?? null;
  const tradeDateTill = constrains.tradeDateRange[1]?.set({ hour: 0, minute: 0, second: 0 }).plus({ days: 1 }) ?? null;

  const upperBound = tradeDateTill === null || tradeDateTill >= item.tradeDate;
  const lowerBound = tradeDateSince === null || tradeDateSince <= item.tradeDate;

  return upperBound && lowerBound;
};

export const portfolioTradeHistoryFilters: FilterFunction<PresentedTradeTicket, PortfolioTradeHistoryFilter>[] = [
  tradeDateFilter,
  (item, constrains) => !constrains.direction || constrains.direction === item.direction,
  issuerIdFilter("issuerId"),
  tickersFilter("ticker"),
  orderTicketIdFilter("orderTicketId"),
  tradeTicketIdFilter("tradeTicketId"),
  faceAmountFilter("amount"),
  spRatingFilter("bondRatingSp"),
  moodysRatingFilter("bondRatingMoodys"),
  postFeePriceFilter("loadedTradePrice"),
  tradeValueFilter("tradeValue"),
  settlementAmountFilter("settlementValue"),
  finalCommissionAmountFilter("finalCommissionAmount"),
];
